import moment from 'moment'

// JS port of api/app/services/timeframe.rb. Resolves a column-level date option
// (the value of params[<accessor_key>Option] set by DateFilterPopover) into a
// concrete { min, max } range so text charts can render absolute dates that
// match what the backend computes at query time.
//
// Returns null for options the server resolves itself (comparison operators
// like 'between'/'less'/'greater'/'on') or unknown values — callers should
// fall back to the literal params in those cases.

const ISO = 'YYYY-MM-DD'

export const DATE_OPTION_LABELS = {
  Yesterday: 'Yesterday',
  'Past 7 days': 'Past 7 days',
  '30': 'Past 30 days',
  '60': 'Past 60 days',
  '90': 'Past 90 days',
  '120': 'Past 120 days',
  'This Week': 'This Week',
  'Last Week': 'Last Week',
  'This Month': 'This Month',
  'Last Month': 'Last Month',
  '1 year': 'More than 1 year ago',
  '2 years': 'More than 2 years ago',
  '3 years': 'More than 3 years ago',
  CY: 'Current Calendar Year',
  LY: 'Last Calendar Year',
  CQ: 'Current Quarter',
  LQ: 'Last Quarter',
  Q1: 'Q1',
  Q2: 'Q2',
  Q3: 'Q3',
  Q4: 'Q4',
  PFY: 'Previous Fiscal Year',
  FY: 'Current Fiscal Year'
}

export const dateOptionLabel = (option) => DATE_OPTION_LABELS[option] || ''

const RELATIVE_OPTIONS = new Set([
  'Yesterday', 'Past 7 days', 'This Week', 'Last Week',
  '30', '60', '90', '120',
  'This Month', 'Last Month',
  '1 year', '2 years', '3 years',
  'CY', 'LY'
])

const QUARTER_OPTIONS = new Set(['Q1', 'Q2', 'Q3', 'Q4', 'CQ', 'LQ'])
const FISCAL_OPTIONS = new Set(['PFY', 'FY'])

const toIso = (m) => m.format(ISO)

// Mirrors Timeframe#get_year: returns the fiscal year start year so that
// `today` always falls inside the [start, start+12mo) range.
const fiscalYearStartYear = (today, startMonth) =>
  today.month() + 1 >= startMonth ? today.year() : today.year() - 1

const fiscalYearStart = (today, startMonth, startDay) => {
  const year = fiscalYearStartYear(today, startMonth)
  return moment({ year, month: startMonth - 1, day: startDay })
}

const relativeRange = (option, today) => {
  switch (option) {
    case 'Yesterday': {
      const y = today.clone().subtract(1, 'days')
      return { min: toIso(y), max: toIso(y) }
    }
    case 'Past 7 days':
      return { min: toIso(today.clone().subtract(7, 'days')), max: toIso(today) }
    case '30':
      return { min: toIso(today.clone().subtract(30, 'days')), max: toIso(today) }
    case '60':
      return { min: toIso(today.clone().subtract(60, 'days')), max: toIso(today) }
    case '90':
      return { min: toIso(today.clone().subtract(90, 'days')), max: toIso(today) }
    case '120':
      return { min: toIso(today.clone().subtract(120, 'days')), max: toIso(today) }
    case 'This Week':
      return {
        min: toIso(today.clone().startOf('isoWeek')),
        max: toIso(today.clone().endOf('isoWeek'))
      }
    case 'Last Week': {
      const lw = today.clone().subtract(1, 'weeks')
      return { min: toIso(lw.clone().startOf('isoWeek')), max: toIso(lw.clone().endOf('isoWeek')) }
    }
    case 'This Month':
      return {
        min: toIso(today.clone().startOf('month')),
        max: toIso(today.clone().endOf('month'))
      }
    case 'Last Month': {
      const lm = today.clone().subtract(1, 'months')
      return { min: toIso(lm.clone().startOf('month')), max: toIso(lm.clone().endOf('month')) }
    }
    case 'CY':
      return {
        min: toIso(today.clone().startOf('year')),
        max: toIso(today.clone().endOf('year'))
      }
    case 'LY': {
      const ly = today.clone().subtract(1, 'years')
      return { min: toIso(ly.clone().startOf('year')), max: toIso(ly.clone().endOf('year')) }
    }
    // "More than N years ago" — open-ended on the left, capped at N years ago.
    case '1 year':
      return { min: '1900-01-01', max: toIso(today.clone().subtract(1, 'years')) }
    case '2 years':
      return { min: '1900-01-01', max: toIso(today.clone().subtract(2, 'years')) }
    case '3 years':
      return { min: '1900-01-01', max: toIso(today.clone().subtract(3, 'years')) }
    default:
      return null
  }
}

// Matches Timeframe#fiscal_quarter_range: quarter N starts at fyStart + (N-1)*3
// months (beginning of month) and ends the day before fyStart + N*3 months
// (end of that prior month).
const fiscalQuarterRange = (quarterNumber, fyStart) => {
  const start = fyStart.clone().add((quarterNumber - 1) * 3, 'months').startOf('month')
  const end = fyStart.clone().add(quarterNumber * 3, 'months').subtract(1, 'days').endOf('month')
  return { min: toIso(start), max: toIso(end) }
}

const currentFiscalQuarterNumber = (today, currentFyStart) => {
  const monthsIntoFy =
    (today.year() - currentFyStart.year()) * 12 +
    (today.month() - currentFyStart.month())
  return Math.floor(monthsIntoFy / 3) + 1
}

const quarterRange = (option, today, currentFyStart, previousFyStart) => {
  switch (option) {
    case 'Q1': return fiscalQuarterRange(1, currentFyStart)
    case 'Q2': return fiscalQuarterRange(2, currentFyStart)
    case 'Q3': return fiscalQuarterRange(3, currentFyStart)
    case 'Q4': return fiscalQuarterRange(4, currentFyStart)
    case 'CQ': return fiscalQuarterRange(currentFiscalQuarterNumber(today, currentFyStart), currentFyStart)
    case 'LQ': {
      const cq = currentFiscalQuarterNumber(today, currentFyStart)
      return cq > 1
        ? fiscalQuarterRange(cq - 1, currentFyStart)
        : fiscalQuarterRange(4, previousFyStart)
    }
    default:
      return null
  }
}

const fiscalYearRange = (option, currentFyStart, previousFyStart) => {
  if (option === 'FY') {
    const end = currentFyStart.clone().add(12, 'months').subtract(1, 'days').endOf('month')
    return { min: toIso(currentFyStart), max: toIso(end) }
  }
  if (option === 'PFY') {
    const end = currentFyStart.clone().subtract(1, 'days').endOf('month')
    return { min: toIso(previousFyStart), max: toIso(end) }
  }
  return null
}

// Returns { min, max } as YYYY-MM-DD strings, or null when the option is
// unknown or one of the comparison operators ('any', 'on', 'less', 'greater',
// 'between', 'is_empty', 'is_not_empty') whose dates already live directly in
// params.
export const resolveDateOption = (option, settings, now = moment()) => {
  if (!option) return null
  const today = moment.isMoment(now) ? now.clone().startOf('day') : moment(now).startOf('day')

  if (RELATIVE_OPTIONS.has(option)) return relativeRange(option, today)

  const startMonth = Number(settings?.fiscal_year_start_month) || 1
  const startDay = Number(settings?.fiscal_year_start_day) || 1
  const currentFyStart = fiscalYearStart(today, startMonth, startDay)
  const previousFyStart = currentFyStart.clone().subtract(1, 'years')

  if (QUARTER_OPTIONS.has(option)) {
    return quarterRange(option, today, currentFyStart, previousFyStart)
  }
  if (FISCAL_OPTIONS.has(option)) {
    return fiscalYearRange(option, currentFyStart, previousFyStart)
  }
  return null
}
